import React, { useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import {
  ArrowRight, ArrowUpRight, CheckCircle2, Clock, Shield, Sparkles, Stethoscope, Plane, Award,
  ChevronRight,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SideContact from '../components/SideContact';
import BeforeAfterCarousel from '../components/BeforeAfterCarousel';
import TreatmentGuide from '../components/TreatmentGuide';
import TreatmentDetails from '../components/TreatmentDetails';
import { useLang } from '../i18n/LanguageContext';
import { useAssets } from '../assets/AssetsContext';
import { findTreatment, serviceCategories, treatmentGalleries, treatmentGuides } from '../mock';

// Hero image pool per category fallback
const categoryHero = {
  body: 'https://images.unsplash.com/photo-1549445069-d1125f7a129c?w=1600&q=80',
  bariatrics: 'https://images.unsplash.com/photo-1591384640699-9a85bd036da2?w=1600&q=80',
  breast: 'https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?w=1600&q=80',
  face: 'https://images.unsplash.com/photo-1552693673-1bf958298935?w=1600&q=80',
  hair: 'https://images.unsplash.com/photo-1512663150964-d8f43c899f76?w=1600&q=80',
  dentistry: 'https://images.unsplash.com/photo-1675526607070-f5cbd71dde92?w=1600&q=80',
};

const benefitDefs = [
  { icon: Award, titleKey: 'tp.benefits.1.title', textKey: 'tp.benefits.1.text' },
  { icon: Shield, titleKey: 'tp.benefits.2.title', textKey: 'tp.benefits.2.text' },
  { icon: Sparkles, titleKey: 'tp.benefits.3.title', textKey: 'tp.benefits.3.text' },
  { icon: Plane, titleKey: 'tp.benefits.4.title', textKey: 'tp.benefits.4.text' },
];

const processDefs = [
  { step: '01', titleKey: 'tp.process.1.title', textKey: 'tp.process.1.text' },
  { step: '02', titleKey: 'tp.process.2.title', textKey: 'tp.process.2.text' },
  { step: '03', titleKey: 'tp.process.3.title', textKey: 'tp.process.3.text' },
  { step: '04', titleKey: 'tp.process.4.title', textKey: 'tp.process.4.text' },
];

const faqDefs = [
  { qKey: 'tp.faq.1.q', aKey: 'tp.faq.1.a' },
  { qKey: 'tp.faq.2.q', aKey: 'tp.faq.2.a' },
  { qKey: 'tp.faq.3.q', aKey: 'tp.faq.3.a' },
  { qKey: 'tp.faq.4.q', aKey: 'tp.faq.4.a' },
];

const TreatmentPage = () => {
  const { slug } = useParams();
  const { t } = useLang();
  const { overrides, apiBase } = useAssets();
  const treatment = findTreatment(slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  if (!treatment) return <Navigate to="/" replace />;

  const { category } = treatment;
  const galleryData = treatmentGalleries[treatment.slug];
  const heroOverride = overrides[`tr.${treatment.slug}.hero`];
  const heroImg = heroOverride
    ? `${apiBase}${heroOverride.url}`
    : (galleryData?.hero || categoryHero[category.id]);
  const galleryImages = (galleryData?.images || []).map((src, i) => {
    const ov = overrides[`tr.${treatment.slug}.gallery.${i}`];
    return ov ? `${apiBase}${ov.url}` : src;
  });
  const guideData = treatmentGuides[treatment.slug];

  return (
    <div className="min-h-screen bg-[#0B3D2E] text-[#ffffff] font-sans-body">
      <Header />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroImg} alt={treatment.label} className="w-full h-full object-cover" loading="lazy" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0B3D2E]/85 via-[#0B3D2E]/70 to-[#0B3D2E]" />
        </div>
        <div className="relative max-w-[1280px] mx-auto px-6 py-24 md:py-32">
          {/* Breadcrumbs */}
          <nav className="flex items-center gap-2 text-xs text-[#ffffff]/70 mb-8">
            <Link to="/" className="hover:text-[#D4AF37]">{t('breadcrumb.home')}</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to="/services" className="hover:text-[#D4AF37]">{t('breadcrumb.services')}</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="hover:text-[#D4AF37]">{t(`cat.${category.id}`, category.label)}</span>
            <ChevronRight className="w-3 h-3" />
            <span className="text-[#D4AF37]">{t(`tr.${treatment.slug}`, treatment.label)}</span>
          </nav>

          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#D4AF37]/30 text-[#D4AF37] text-xs tracking-[0.22em] uppercase mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]" />
            {t(`cat.${category.id}`, category.label)}
          </div>
          <h1 className="font-serif-display text-5xl md:text-7xl leading-[1.05] mb-6 max-w-3xl">
            {t(`tr.${treatment.slug}`, treatment.label)} <span className="gold-text italic">{t('treatment.atGoldCare')}</span>
          </h1>
          <p className="text-lg md:text-xl text-[#ffffff]/80 max-w-2xl mb-10 leading-relaxed">
            {t('treatment.intro', null, { name: treatment.label.toLowerCase() })}
          </p>
          <div className="flex flex-wrap gap-4">
            <Link to="/#contact-form" className="btn-gold inline-flex items-center gap-3 px-8 py-3.5 rounded-full text-sm font-semibold">
              {t('cta.getFreeQuote')}
              <ArrowRight className="w-4 h-4" />
            </Link>
            <a href={`https://wa.me/${treatment && 905313775419}`} target="_blank" rel="noopener noreferrer"
               className="btn-ghost-gold inline-flex items-center gap-2 px-8 py-3.5 rounded-full text-sm">
              {t('cta.chatWhatsApp')}
              <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>

          {/* Quick stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-14 max-w-3xl">
            {[
              { icon: Clock, k: t('treatment.quick.procedure'), v: t('treatment.procedureHours') },
              { icon: Stethoscope, k: t('treatment.quick.anesthesia'), v: t('treatment.quick.anesthesia.val') },
              { icon: Plane, k: t('treatment.quick.stay'), v: t('treatment.quick.stay.val') },
              { icon: Shield, k: t('treatment.quick.guarantee'), v: t('treatment.quick.guarantee.val') },
            ].map(({ icon: Icon, k, v }) => (
              <div key={k} className="bg-[#124D3A]/60 border border-[#D4AF37]/15 rounded-2xl p-4">
                <Icon className="w-5 h-5 text-[#D4AF37] mb-2" />
                <div className="text-[11px] uppercase tracking-[0.18em] text-[#ffffff]/60">{k}</div>
                <div className="text-[#ffffff] font-medium mt-0.5">{v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="py-20 md:py-28 bg-[#f5ede2] text-[#0B3D2E]">
        <div className="max-w-[1040px] mx-auto px-6">
          <div className="grid md:grid-cols-5 gap-10">
            <div className="md:col-span-2">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#0B3D2E]/20 text-[#0B3D2E]/70 text-xs tracking-[0.22em] uppercase mb-5">
                {t('tp.overview')}
              </div>
              <h2 className="font-serif-display text-4xl md:text-5xl leading-tight">
                {t('tp.whatIs')} <span className="peach-text italic">{treatment.label}?</span>
              </h2>
            </div>
            <div className="md:col-span-3 space-y-5 text-[17px] leading-relaxed text-[#0B3D2E]/85">
              <p>{t('tp.overview.p1', null, { name: treatment.label, category: category.label.toLowerCase() })}</p>
              <p>{t('tp.overview.p2')}</p>
              <p>{t('tp.overview.p3', null, { name: treatment.label })}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Detailed treatment explanation */}
      <TreatmentDetails slug={treatment.slug} treatmentLabel={t(`tr.${treatment.slug}`, treatment.label)} />

      {/* Your Guide to Successful ... */}
      {guideData && (
        <TreatmentGuide
          heading={guideData.heading}
          description={guideData.description}
          stats={guideData.stats}
        />
      )}

      {/* Before & After gallery */}
      {galleryImages.length > 0 && (
        <BeforeAfterCarousel
          title={`${treatment.label} Transformation:`}
          treatmentLabel={treatment.label}
          images={galleryImages}
        />
      )}

      {/* Benefits */}
      <section className="py-20 md:py-28 bg-[#0B3D2E]">
        <div className="max-w-[1280px] mx-auto px-6">
          <div className="text-center mb-14 max-w-2xl mx-auto">
            <h2 className="font-serif-display text-4xl md:text-5xl leading-tight mb-4">
              {t('section.whyChoose')} <span className="peach-text italic">Gold Care Esthetic</span>
            </h2>
            <p className="text-[#ffffff]/70">
              {t('tp.why.sub', null, { name: treatment.label.toLowerCase() })}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {benefitDefs.map(({ icon: Icon, titleKey, textKey }) => (
              <div key={titleKey} className="bg-[#124D3A] border border-[#D4AF37]/15 rounded-3xl p-7 card-hover">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5"
                     style={{ background: 'linear-gradient(135deg, #F6E3A1 0%, #D4AF37 50%, #A8832E 100%)' }}>
                  <Icon className="w-5 h-5 text-[#0B3D2E]" />
                </div>
                <h3 className="font-serif-display text-xl text-[#ffffff] mb-2">{t(titleKey)}</h3>
                <p className="text-sm text-[#ffffff]/70 leading-relaxed">{t(textKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 md:py-28 bg-[#f5ede2] text-[#0B3D2E]">
        <div className="max-w-[1280px] mx-auto px-6">
          <div className="text-center mb-14 max-w-2xl mx-auto">
            <h2 className="font-serif-display text-4xl md:text-5xl leading-tight mb-4">
              {t('section.yourJourney', null, { name: treatment.label })}
            </h2>
            <p className="text-[#0B3D2E]/65">{t('section.journey.desc')}</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {processDefs.map((p) => (
              <div key={p.step} className="bg-white rounded-3xl p-7 card-hover border border-[#0B3D2E]/5">
                <div className="font-serif-display text-5xl text-[#D4AF37] mb-3 leading-none">{p.step}</div>
                <h3 className="font-serif-display text-xl mb-2">{t(p.titleKey)}</h3>
                <p className="text-sm text-[#0B3D2E]/65 leading-relaxed">{t(p.textKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 md:py-28 bg-[#0B3D2E]">
        <div className="max-w-[960px] mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="font-serif-display text-4xl md:text-5xl leading-tight">
              {t('section.faq')}
            </h2>
          </div>
          <div className="space-y-3">
            {faqDefs.map((f, i) => (
              <details key={i} className="group bg-[#124D3A] border border-[#D4AF37]/15 rounded-2xl p-6 open:border-[#D4AF37]/40 transition-colors">
                <summary className="flex items-center justify-between cursor-pointer list-none">
                  <span className="font-serif-display text-lg md:text-xl text-[#ffffff] pr-8">{t(f.qKey)}</span>
                  <span className="w-9 h-9 rounded-full bg-[#0B3D2E] border border-[#D4AF37]/25 flex items-center justify-center text-[#D4AF37] group-open:rotate-45 transition-transform shrink-0">+</span>
                </summary>
                <p className="mt-4 text-[15px] text-[#ffffff]/75 leading-relaxed">{t(f.aKey)}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Related treatments */}
      <section className="py-20 md:py-28 bg-[#072A1F]">
        <div className="max-w-[1280px] mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
            <div>
              <div className="text-[11px] tracking-[0.22em] uppercase text-[#D4AF37] mb-2">{t('section.moreFrom', null, { category: t(`cat.${category.id}`, category.label) })}</div>
              <h2 className="font-serif-display text-3xl md:text-4xl leading-tight">
                {t('section.otherTreatments')}
              </h2>
            </div>
            <Link to="/services" className="btn-ghost-gold inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-sm self-start md:self-auto">
              {t('section.allServices')}
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {category.treatments
              .filter((tr) => tr.slug !== treatment.slug)
              .map((tr) => (
                <Link
                  key={tr.slug}
                  to={`/treatments/${tr.slug}`}
                  className="group bg-[#124D3A] border border-[#D4AF37]/15 rounded-2xl p-4 hover:border-[#D4AF37]/50 hover:bg-[#124D3A] transition-all"
                >
                  <div className="font-serif-display text-[15px] text-[#ffffff] group-hover:text-[#D4AF37] transition-colors leading-snug">
                    {t(`tr.${tr.slug}`, tr.label)}
                  </div>
                  <div className="flex items-center gap-1 text-[11px] text-[#ffffff]/55 mt-2">
                    {t('cta.learnMore')}
                    <ArrowUpRight className="w-3 h-3" />
                  </div>
                </Link>
              ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-[#0B3D2E] relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/3 w-[40rem] h-[40rem] rounded-full bg-[#D4AF37]/10 blur-[120px] pointer-events-none" />
        </div>
        <div className="relative max-w-[960px] mx-auto px-6 text-center">
          <CheckCircle2 className="w-12 h-12 text-[#D4AF37] mx-auto mb-6" strokeWidth={1.5} />
          <h2 className="font-serif-display text-4xl md:text-6xl leading-tight mb-5">
            {t('section.ready')} <span className="peach-text italic">{t(`tr.${treatment.slug}`, treatment.label)}?</span>
          </h2>
          <p className="text-[#ffffff]/75 text-lg mb-10 max-w-xl mx-auto">
            {t('section.ready.sub')}
          </p>
          <Link to="/#contact-form" className="btn-gold inline-flex items-center gap-3 px-10 py-4 rounded-full text-base font-semibold group">
            {t('cta.bookFreeConsultation')}
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </section>

      <Footer />
      <SideContact />
    </div>
  );
};

export default TreatmentPage;

export const ServicesIndex = () => {
  const { t } = useLang();
  useEffect(() => { window.scrollTo({ top: 0 }); }, []);
  return (
    <div className="min-h-screen bg-[#0B3D2E] text-[#ffffff] font-sans-body">
      <Header />
      <section className="py-20 md:py-28">
        <div className="max-w-[1280px] mx-auto px-6">
          <div className="text-center mb-14 max-w-2xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#D4AF37]/30 text-[#D4AF37] text-xs tracking-[0.22em] uppercase mb-5">
              {t('servicesIndex.kicker')}
            </div>
            <h1 className="font-serif-display text-5xl md:text-6xl leading-tight">
              {t('servicesIndex.title')} <span className="peach-text italic">{t('servicesIndex.title.italic')}</span>
            </h1>
            <p className="text-[#ffffff]/70 mt-4">
              {t('servicesIndex.sub')}
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {serviceCategories.map((cat) => (
              <div key={cat.id} className="bg-[#124D3A] border border-[#D4AF37]/15 rounded-3xl overflow-hidden">
                <div className="aspect-[16/9] overflow-hidden">
                  <img src={cat.image} alt={cat.label} className="w-full h-full object-cover" loading="lazy" />
                </div>
                <div className="p-6">
                  <h3 className="font-serif-display text-2xl text-[#ffffff] mb-4">{t(`cat.${cat.id}`, cat.label)}</h3>
                  <ul className="space-y-2">
                    {cat.treatments.map((tr) => (
                      <li key={tr.slug}>
                        <Link
                          to={`/treatments/${tr.slug}`}
                          className="flex items-center justify-between text-sm text-[#ffffff]/80 hover:text-[#D4AF37] py-1.5 transition-colors"
                        >
                          {t(`tr.${tr.slug}`, tr.label)}
                          <ArrowUpRight className="w-3.5 h-3.5" />
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      <Footer />
      <SideContact />
    </div>
  );
};
